import React, { useMemo, useRef, useCallback, useEffect, useState } from 'react'
import { Box } from '@mantine/core'
import type { Textbox } from '../app/models/shootTextbox'
import { drawNormalizedTextboxes, getObjectFitContainRect, parseTextBoxes } from '../app/utils/shootsCoords'

export type ShootsCanvasMediaType = 'photo' | 'video';

const FALLBACK_ASPECT = 16 / 9

export interface ShootsCanvasRendererProps {
  mediaUrl: string;
  mediaType: ShootsCanvasMediaType;
  /** Raw JSON from the server (`textBoxesJson` column). */
  textBoxesJson: string;
  width: number;
  height: number;
  /**
   * Intrinsic width / height of the media (optional). When set, drives the outer container `aspect-ratio`
   * so layout matches the shoot; otherwise uses JSON `mediaAspect` (v2) or `width`/`height` or 16:9.
   */
  mediaAspect?: number;
  /**
   * When `mediaType === "video"`, notify parent (e.g. ShootsEditor) for loop / freeze listeners.
   */
  onVideoMount?: (video: HTMLVideoElement) => void;
  /** Editor preview: autoplay muted video under the text overlay. Default false (feed-style). */
  videoAutoPlay?: boolean;
  /** When false, render only the overlay canvas (for feed/detail layering over existing media). */
  renderMedia?: boolean;
}

/** `object-fit: contain` — letterbox with black bars (video frame into canvas bitmap). */
function drawVideoFrameContain (ctx: CanvasRenderingContext2D, video: HTMLVideoElement, cw: number, ch: number): void {
  const vw = video.videoWidth
  const vh = video.videoHeight
  if (!vw || !vh) return
  const scale = Math.min(cw / vw, ch / vh)
  const dw = vw * scale
  const dh = vh * scale
  const dx = (cw - dw) / 2
  const dy = (ch - dh) / 2
  ctx.drawImage(video, dx, dy, dw, dh)
}

function paintCanvas (
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  mediaType: ShootsCanvasMediaType,
  video: HTMLVideoElement | null,
  boxes: readonly Textbox[],
  mediaAspect: number,
  renderMedia: boolean
): void {
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, cw, ch)

  if (renderMedia && mediaType === 'video') {
    ctx.fillStyle = '#000'
    ctx.fillRect(0, 0, cw, ch)
    if (video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      drawVideoFrameContain(ctx, video, cw, ch)
    }
  }

  /*
   * Legacy v1: root JSON array stored fixed logical pixel coordinates, not normalized fractions.
   * Rendering those values as normalized would misplace text on real media. We intentionally draw
   * no overlays for that shape until callers migrate to v2 (see `parseTextBoxes` + `legacyRootArray`).
   */
  const containRect = getObjectFitContainRect(cw, ch, mediaAspect)
  if (containRect.w <= 0 || containRect.h <= 0) return
  ctx.save()
  ctx.translate(containRect.x, containRect.y)
  drawNormalizedTextboxes(ctx, boxes, containRect.w, containRect.h, { showEditorChrome: false })
  ctx.restore()
}

const mediaBaseStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  display: 'block',
  zIndex: 1
}

/**
 * Shoot media with canvas overlay (z-index 10) for JSON textboxes.
 * Photo: transparent canvas over `<img>`; text uses opacity/color/font.
 * Video: each paint draws the current video frame into the canvas, then text; the `<video>` is hidden (opacity 0) so only the canvas shows.
 */
export default function ShootsCanvasRenderer ({
  mediaUrl,
  mediaType,
  textBoxesJson,
  width,
  height,
  mediaAspect: mediaAspectProp,
  onVideoMount,
  videoAutoPlay = false,
  renderMedia = true
}: ShootsCanvasRendererProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const reportedVideoMount = useRef(false)
  const [videoNode, setVideoNode] = useState<HTMLVideoElement | null>(null)
  const legacyWarnedRef = useRef<string | null>(null)

  const parsed = useMemo(() => parseTextBoxes(textBoxesJson ?? ''), [textBoxesJson])

  const boxesToDraw = useMemo(() => {
    if (parsed.legacyRootArray) return []
    return parsed.boxes
  }, [parsed])

  useEffect(() => {
    if (!parsed.legacyRootArray || parsed.boxes.length === 0) return
    if (legacyWarnedRef.current === textBoxesJson) return
    legacyWarnedRef.current = textBoxesJson ?? ''
    console.warn(
      '[ShootsCanvasRenderer] Legacy v1 textBoxesJson (root array) is not rendered — coordinates are not normalized. Expect no overlay until content uses v2.'
    )
  }, [parsed.legacyRootArray, parsed.boxes.length, textBoxesJson])

  const layoutAspect = useMemo(() => {
    if (mediaAspectProp != null && Number.isFinite(mediaAspectProp) && mediaAspectProp > 0) {
      return mediaAspectProp
    }
    if (parsed.mediaAspect != null && Number.isFinite(parsed.mediaAspect) && parsed.mediaAspect > 0) {
      return parsed.mediaAspect
    }
    if (width > 0 && height > 0) return width / height
    return FALLBACK_ASPECT
  }, [mediaAspectProp, parsed.mediaAspect, width, height])

  const paintOverlay = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return
    paintCanvas(
      ctx,
      width,
      height,
      mediaType,
      mediaType === 'video' ? videoNode : null,
      boxesToDraw,
      layoutAspect,
      renderMedia
    )
  }, [boxesToDraw, width, height, mediaType, videoNode, layoutAspect, renderMedia])

  useEffect(() => {
    paintOverlay()
  }, [paintOverlay])

  const setVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      setVideoNode(el)
      if (el && onVideoMount && !reportedVideoMount.current) {
        reportedVideoMount.current = true
        onVideoMount(el)
      }
      if (!el) reportedVideoMount.current = false
    },
    [onVideoMount]
  )

  useEffect(() => {
    if (mediaType !== 'video' || !videoNode) return
    const onFrame = () => paintOverlay()
    videoNode.addEventListener('loadeddata', onFrame)
    videoNode.addEventListener('seeked', onFrame)
    videoNode.addEventListener('timeupdate', onFrame)
    onFrame()
    return () => {
      videoNode.removeEventListener('loadeddata', onFrame)
      videoNode.removeEventListener('seeked', onFrame)
      videoNode.removeEventListener('timeupdate', onFrame)
    }
  }, [mediaType, mediaUrl, videoNode, paintOverlay])

  useEffect(() => {
    if (mediaType === 'video' && videoNode && videoAutoPlay) {
      videoNode.play().catch(() => undefined)
    }
  }, [mediaType, mediaUrl, videoAutoPlay, videoNode])

  useEffect(() => {
    if (mediaType === 'photo') {
      setVideoNode(null)
      reportedVideoMount.current = false
    }
  }, [mediaType])

  return (
    <Box
      sx={{
        position: renderMedia ? 'relative' : 'absolute',
        inset: renderMedia ? undefined : 0,
        width: '100%',
        height: renderMedia ? undefined : '100%',
        maxWidth: '100%',
        backgroundColor: renderMedia ? '#000' : 'transparent',
        borderRadius: renderMedia ? 6 : undefined,
        overflow: 'hidden',
        aspectRatio: renderMedia ? `${layoutAspect}` : undefined,
        zIndex: 2,
        pointerEvents: 'none'
      }}
    >
      {renderMedia && mediaType === 'photo' ? (
        <img
          src={mediaUrl}
          alt=""
          crossOrigin={mediaUrl.startsWith('blob:') || mediaUrl.startsWith('data:') ? undefined : 'anonymous'}
          onLoad={paintOverlay}
          onError={paintOverlay}
          style={mediaBaseStyle}
        />
      ) : renderMedia ? (
        <video
          ref={setVideoRef}
          src={mediaUrl}
          muted
          playsInline
          autoPlay={videoAutoPlay}
          controls={false}
          style={{
            ...mediaBaseStyle,
            /* Frames are composited onto the canvas; keep element for decode/timing. */
            opacity: 0
          }}
        />
      ) : null}
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          zIndex: 2,
          pointerEvents: 'none',
          display: 'block'
        }}
      />
    </Box>
  )
}
